import { AlertTriangle, X } from "lucide-react";

const oemFails = [
  {
    title: "Inclined main impeller shield",
    detail: "Big OD/ID ratio — low external force tips the shield axis. It gets stuck mid-stroke.",
  },
  {
    title: "Undersized secondary pump",
    detail: "Sub-10mm piston pump is hyper-sensitive to debris. Weak return spring can't recover.",
  },
  {
    title: "Main piston seizes",
    detail: "Limited design space means a weak spring. Once it sticks — coolant flow drops to zero.",
  },
];

const aftermarketFails = [
  {
    title: "Collector cuts the flow",
    detail: "Turbulence under the main impeller. Cavitation at high RPM kills the secondary pump.",
  },
  {
    title: "Shield function loss",
    detail: "Unbalanced spring vs. rubber-impeller pressure — the shield stops obeying.",
  },
  {
    title: "Rubber impeller wear",
    detail: "Abrasive particles from the cooling system shred the vanes. Pressure collapses.",
  },
];

export default function Problem() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-28 lg:py-36 bg-[#F4F6FA] border-y border-[#C8D3DF] overflow-hidden"
    >
      <div className="absolute inset-0 diagonal-stripes opacity-15 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <div className="max-w-3xl mb-16 reveal">
          <div className="flex items-center gap-3 mb-5">
            <AlertTriangle size={16} className="text-[#FF5722]" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#FF5722] uppercase">
              VAG Coolant Pump · The Failure Pattern
            </span>
          </div>
          <h2 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A2232] leading-[0.95]">
            Every other pump <br />
            fails the <span className="text-[#FF5722]">same way</span>.
          </h2>
          <p className="mt-6 font-plex text-base lg:text-lg text-[#536073] max-w-2xl leading-relaxed">
            When their switching mechanism fails, they fail <span className="text-[#1A2232] font-semibold">closed</span>.
            Coolant stops circulating. Within minutes, the engine warps. Within hours, the warranty claim hits your desk.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* OEM */}
          <FailureCard
            tag="OEM"
            subtitle="OEM Wobble-Plate Coolant Pump"
            title="Wobble-Plate Axial Piston Pump"
            image="/assets/oem_pump_photo.webp"
            fails={oemFails}
            testid="failure-card-oem"
          />
          {/* Aftermarket */}
          <FailureCard
            tag="Aftermarket"
            subtitle="Aftermarket Switchable Coolant Pump"
            title="Rubber-Impeller Shutter Pump"
            image="/assets/aftermarket_pump_photo.jpg"
            fails={aftermarketFails}
            testid="failure-card-aftermarket"
          />
        </div>
      </div>
    </section>
  );
}

function FailureCard({ tag, subtitle, title, image, fails, testid }) {
  return (
    <div
      data-testid={testid}
      className="reveal relative bg-white border border-[#C8D3DF] rounded-sm overflow-hidden group"
    >
      {/* image header */}
      <div className="relative aspect-[16/10] overflow-hidden border-b border-[#C8D3DF] bg-white">
        <img
          src={image}
          alt={`${title} — VAG switchable coolant pump reference photograph`}
          className="w-full h-full object-cover opacity-95 group-hover:opacity-100 transition-opacity duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent" />
        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-[#FF5722]/15 border border-[#FF5722]/40 backdrop-blur-md">
          <span className="w-1.5 h-1.5 bg-[#FF5722]" />
          <span className="font-mono text-[10px] tracking-[0.2em] text-[#FF5722] uppercase">
            {tag}
          </span>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#536073] uppercase mb-1">
            {subtitle}
          </div>
          <div className="font-chivo font-bold text-xl text-[#1A2232]">{title}</div>
        </div>
      </div>

      <div className="p-6 lg:p-8 space-y-4">
        {fails.map((f, i) => (
          <div key={i} className="flex gap-4 pb-4 border-b border-[#E8EEF6] last:border-0 last:pb-0">
            <div className="shrink-0 w-7 h-7 rounded-full bg-[#FF5722]/15 border border-[#FF5722]/40 flex items-center justify-center mt-0.5">
              <X size={12} className="text-[#FF5722]" />
            </div>
            <div>
              <div className="font-chivo font-bold text-[#1A2232] text-[15px]">{f.title}</div>
              <p className="font-plex text-sm text-[#536073] mt-1 leading-relaxed">{f.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
